import React, { useState } from 'react';
import NavigationBar from './NavigationBar';
import './MaterialWeightCalculator.css';

interface Material {
  id: number;
  name: string;
  density: number;
}

interface CalculationResult {
  value: number;
  unit: string;
  formula: string;
}

const MaterialWeightCalculator: React.FC = () => {
  const materials: Material[] = [
    { id: 1, name: 'C1720', density: 8.36 },
    { id: 2, name: '7合金', density: 8.71 },
    { id: 3, name: '8·11合金', density: 8.75 },
    { id: 4, name: 'YCUT-FX', density: 8.68 },
    { id: 5, name: 'C2680(H65)', density: 8.5 },
    { id: 6, name: 'C2800(H62)', density: 8.5 },
    { id: 7, name: 'C2600(H70)', density: 8.5 },
    { id: 8, name: 'C5191', density: 8.83 },
    { id: 9, name: 'C5210', density: 8.8 },
    { id: 10, name: 'C7035', density: 8.82 },
    { id: 11, name: 'C7025', density: 8.8 },
    { id: 12, name: 'C1020', density: 8.93 },
    { id: 13, name: 'C1100(T2)', density: 8.93 },
    { id: 14, name: 'C18400', density: 8.9 },
    { id: 15, name: 'C18140', density: 8.9 },
    { id: 16, name: 'C18150', density: 8.9 },
    { id: 17, name: 'C7521', density: 8.73 },
    { id: 18, name: 'C7701', density: 8.7 },
    { id: 19, name: 'SUS301', density: 7.93 },
    { id: 20, name: 'SUS304', density: 7.93 },
  ];

  const [selectedMaterial, setSelectedMaterial] = useState<string>('');
  const [density, setDensity] = useState<string>('');
  const [shape, setShape] = useState<string>('rectangle');

  // 长方体参数
  const [length, setLength] = useState<string>('');
  const [thickness, setThickness] = useState<string>('');
  const [width, setWidth] = useState<string>('');
  const [weight, setWeight] = useState<string>('');

  // 空心管参数
  const [outerDiameter, setOuterDiameter] = useState<string>('');
  const [innerDiameter, setInnerDiameter] = useState<string>('');
  const [pipeWeight, setPipeWeight] = useState<string>('');

  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleMaterialChange = (materialId: string) => {
    setSelectedMaterial(materialId);

    if (materialId) {
      const material = materials.find(m => m.id.toString() === materialId);
      if (material) {
        setDensity(material.density.toString());
      }
    } else {
      setDensity('');
    }
  };

  const calculateRectangle = () => {
    const L = parseFloat(length);
    const w = parseFloat(width);
    const t = parseFloat(thickness);
    const m = parseFloat(weight);
    const rho = parseFloat(density);

    if (!rho) {
      alert('请先选择材料！');
      return;
    }

    // 长方体重量计算公式: m = L * w * t * ρ / 1000
    if (L && w && t) {
      const calculatedWeight = (L * w * t * rho) / 1000;
      setResult({
        value: calculatedWeight,
        unit: 'kg',
        formula: 'm = L × w × t × ρ / 1000',
      });
      return;
    }

    // 长方体长度计算公式: L = m × 1000 / (w × t × ρ)
    if (m && w && t) {
      const calculatedLength = (m * 1000) / (w * t * rho);
      setResult({
        value: calculatedLength,
        unit: 'm',
        formula: 'L = m × 1000 / (w × t × ρ)',
      });
      return;
    }

    alert('请输入足够的参数进行计算！');
  };

  const calculateHollowPipe = () => {
    const D = parseFloat(outerDiameter);
    const d = parseFloat(innerDiameter);
    const w = parseFloat(width);
    const m = parseFloat(pipeWeight);
    const rho = parseFloat(density);

    if (!rho) {
      alert('请先选择材料！');
      return;
    }

    // 空心管重量计算公式: m = π × ρ × (w/10) × (D² - d²) / 400 / 1000
    if (D && d && w) {
      const calculatedWeight =
        (Math.PI * rho * (w / 10) * (D * D - d * d)) / 400 / 1000;
      setResult({
        value: calculatedWeight,
        unit: 'kg',
        formula: 'm = π × ρ × (w/10) × (D² - d²) / 400 / 1000',
      });
      return;
    }

    // 空心管外径计算公式: D = √(4 × m × 1000 / (ρ × π × (w/10)) + (d²/100)) × 10
    if (m && d && w) {
      const calculatedDiameter =
        Math.sqrt((4 * m * 1000) / (rho * Math.PI * (w / 10)) + (d * d) / 100) *
        10;
      setResult({
        value: calculatedDiameter,
        unit: 'mm',
        formula: 'D = √(4 × m × 1000 / (ρ × π × (w/10)) + (d²/100)) × 10',
      });
      return;
    }

    alert('请输入足够的参数进行计算！');
  };

  const calculate = () => {
    if (shape === 'rectangle') {
      calculateRectangle();
    } else {
      calculateHollowPipe();
    }
  };

  const clearForm = () => {
    setLength('');
    setThickness('');
    setWidth('');
    setWeight('');
    setOuterDiameter('');
    setInnerDiameter('');
    setPipeWeight('');
    setResult(null);
  };

  return (
    <div className="material-weight-calculator">
      <NavigationBar title="材料重量计算器" />

      <div className="calculator-section">
        <div className="form-group">
          <label>材料牌号：</label>
          <select
            value={selectedMaterial}
            onChange={e => handleMaterialChange(e.target.value)}
          >
            <option value="">请选择材料</option>
            {materials.map(material => (
              <option key={material.id} value={material.id}>
                {material.name} (ρ={material.density} g/cm³)
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>密度 ρ (g/cm³)：</label>
          <input
            type="number"
            value={density}
            onChange={e => setDensity(e.target.value)}
            placeholder="选择材料后自动填充"
            step="0.01"
            readOnly
          />
        </div>

        <div className="form-group">
          <label>形状：</label>
          <select
            value={shape}
            onChange={e => {
              setShape(e.target.value);
              clearForm();
            }}
          >
            <option value="rectangle">长方体</option>
            <option value="hollow-pipe">空心管</option>
          </select>
        </div>

        {shape === 'rectangle' ? (
          <div className="shape-section">
            <div className="formula-display">
              <h4>长方体计算公式</h4>
              <div className="formula">重量：m = L × w × t × ρ / 1000</div>
              <div className="formula">长度：L = m × 1000 / (w × t × ρ)</div>
            </div>

            <div className="input-row">
              <div className="form-group">
                <label>长度 L (m)：</label>
                <input
                  type="number"
                  value={length}
                  onChange={e => setLength(e.target.value)}
                  placeholder="输入长度"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>宽度 w (mm)：</label>
                <input
                  type="number"
                  value={width}
                  onChange={e => setWidth(e.target.value)}
                  placeholder="输入宽度"
                  step="0.01"
                />
              </div>
            </div>

            <div className="input-row">
              <div className="form-group">
                <label>厚度 t (mm)：</label>
                <input
                  type="number"
                  value={thickness}
                  onChange={e => setThickness(e.target.value)}
                  placeholder="输入厚度"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>重量 m (kg)：</label>
                <input
                  type="number"
                  value={weight}
                  onChange={e => setWeight(e.target.value)}
                  placeholder="输入重量"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="shape-section">
            <div className="formula-display">
              <h4>空心管计算公式</h4>
              <div className="formula">
                重量：m = π × ρ × (w/10) × (D² - d²) / 400 / 1000
              </div>
              <div className="formula">
                外径：D = √(4 × m × 1000 / (ρ × π × (w/10)) + (d²/100)) × 10
              </div>
            </div>

            <div className="input-row">
              <div className="form-group">
                <label>壁厚 w (mm)：</label>
                <input
                  type="number"
                  value={width}
                  onChange={e => setWidth(e.target.value)}
                  placeholder="输入壁厚"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>内径 d (mm)：</label>
                <input
                  type="number"
                  value={innerDiameter}
                  onChange={e => setInnerDiameter(e.target.value)}
                  placeholder="输入内径"
                  step="0.01"
                />
              </div>
            </div>

            <div className="input-row">
              <div className="form-group">
                <label>外径 D (mm)：</label>
                <input
                  type="number"
                  value={outerDiameter}
                  onChange={e => setOuterDiameter(e.target.value)}
                  placeholder="输入外径"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>重量 m (kg)：</label>
                <input
                  type="number"
                  value={pipeWeight}
                  onChange={e => setPipeWeight(e.target.value)}
                  placeholder="输入重量"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        )}

        <div className="button-group">
          <button className="calculate-btn" onClick={calculate}>
            计算
          </button>
          <button className="clear-btn" onClick={clearForm}>
            清空
          </button>
        </div>

        {result && (
          <div className="result">
            <h3>计算结果</h3>
            <div className="result-content">
              <div className="result-value">
                <span className="value-number">{result.value.toFixed(2)}</span>
                <span className="value-unit">{result.unit}</span>
              </div>
              <div className="result-formula">
                <strong>计算公式：</strong> {result.formula}
              </div>
            </div>
          </div>
        )}

        <div className="parameter-info">
          <h4>参数说明</h4>
          <div className="parameter-grid">
            <div className="parameter-item">
              <span className="param-name">L:</span>
              <span className="param-desc">长度 (m)</span>
            </div>
            <div className="parameter-item">
              <span className="param-name">w:</span>
              <span className="param-desc">宽度/壁厚 (mm)</span>
            </div>
            <div className="parameter-item">
              <span className="param-name">t:</span>
              <span className="param-desc">厚度 (mm)</span>
            </div>
            <div className="parameter-item">
              <span className="param-name">ρ:</span>
              <span className="param-desc">密度 (g/cm³)</span>
            </div>
            <div className="parameter-item">
              <span className="param-name">m:</span>
              <span className="param-desc">重量 (kg)</span>
            </div>
            <div className="parameter-item">
              <span className="param-name">D:</span>
              <span className="param-desc">外径 (mm)</span>
            </div>
            <div className="parameter-item">
              <span className="param-name">d:</span>
              <span className="param-desc">内径 (mm)</span>
            </div>
            <div className="parameter-item">
              <span className="param-name">π:</span>
              <span className="param-desc">圆周率 (3.14159)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialWeightCalculator;
